// Extracted from the web lap-brief dependency graph. Shared by API and web.
import { type TrackContributionCornerApex } from '../../index.js';
import { buildMyChronLapsFromGps } from './gpsLapDetection.js';
export type MyChronGpsPoint = {
  timecodeMs: number;
  lat: number;
  lon: number;
  alt: number;
  speed: number;
  fix?: number;
  satellites?: number;
};

export type MyChronLapRecord = {
  segment: number;
  lapNumber: number;
  durationMs: number;
  endTimeMs: number;
};

export type MyChronSeriesSample = {
  timecodeMs: number;
  value: number;
};

export type MyChronChannelMeta = {
  index: number;
  shortName: string;
  longName: string;
  unit: string;
  decimals?: number;
};

export type MyChronSessionMetadata = {
  logDate?: string;
  logTime?: string;
  driver?: string;
  vehicle?: string;
  venue?: string;
  circuit?: {
    contributionId?: string;
    trackId?: string;
    name?: string;
    cornerApexSuggestions?: TrackContributionCornerApex[];
  };
};

export type MyChronSessionExport = {
  format: string;
  channels: MyChronChannelMeta[];
  laps: MyChronLapRecord[];
  gps: MyChronGpsPoint[];
  series: Record<string, MyChronSeriesSample[]>;
  metadata?: MyChronSessionMetadata;
  stats?: {
    gpsSamples?: number;
    singleSamples?: number;
    multiSamples?: number;
    groupSamples?: number;
    skippedBytes?: number;
    recordedAtMs?: number;
  };
};

export function rawLoggerLaps(laps: MyChronLapRecord[] | null | undefined) {
  if (!Array.isArray(laps)) {
    return [];
  }

  return laps
    .filter((lap) => (lap.segment ?? 0) === 0 && lap.durationMs > 0)
    .sort((left, right) => left.lapNumber - right.lapNumber);
}

export function resolveRawLoggerLaps(session: MyChronSessionExport): MyChronLapRecord[] {
  const fromLogger = rawLoggerLaps(session.laps);
  if (fromLogger.length > 0) {
    return fromLogger;
  }

  if (session.gps.length >= 12) {
    return buildMyChronLapsFromGps(session.gps);
  }

  return [];
}

export function racingLaps(laps: MyChronLapRecord[] | null | undefined) {
  return rawLoggerLaps(laps).filter((lap) => lap.durationMs >= 15_000 && lap.durationMs <= 120_000);
}

export type LapExclusionReason = 'out' | 'in' | 'invalid' | 'other';

export type LapExclusion = {
  lapNumber: number;
  reason: LapExclusionReason;
};

export function racingLapsForAnalysis(
  session: MyChronSessionExport,
  exclusions: LapExclusion[] = [],
) {
  const excluded = new Set(exclusions.map((entry) => entry.lapNumber));
  return racingLaps(session.laps).filter((lap) => !excluded.has(lap.lapNumber));
}

export function lapWindow(lap: MyChronLapRecord) {
  return {
    startMs: lap.endTimeMs - lap.durationMs,
    endMs: lap.endTimeMs,
  };
}

function seriesInWindow(series: MyChronSeriesSample[] | undefined, startMs: number, endMs: number) {
  if (!series?.length) {
    return [];
  }

  return series.filter((sample) => sample.timecodeMs >= startMs && sample.timecodeMs <= endMs);
}

function maxInWindow(series: MyChronSeriesSample[] | undefined, startMs: number, endMs: number) {
  const values = seriesInWindow(series, startMs, endMs).map((sample) => sample.value);
  return values.length === 0
    ? null
    : values.reduce((max, value) => Math.max(max, value), -Infinity);
}

function medianInWindow(series: MyChronSeriesSample[] | undefined, startMs: number, endMs: number) {
  const values = seriesInWindow(series, startMs, endMs)
    .map((sample) => sample.value)
    .sort((left, right) => left - right);

  if (values.length === 0) {
    return null;
  }

  const mid = Math.floor(values.length / 2);
  return values.length % 2 === 1 ? values[mid]! : (values[mid - 1]! + values[mid]!) / 2;
}

export function driverLongitudinalG(
  accX: number | null,
  accXBaseline: number | null,
): number | null {
  if (accX === null) {
    return null;
  }

  if (accXBaseline === null) {
    return accX;
  }

  return accX - accXBaseline;
}

export function driverLateralG(accY: number | null): number | null {
  if (accY === null) {
    return null;
  }

  return -accY;
}

function gpsInWindow(gps: MyChronGpsPoint[], startMs: number, endMs: number) {
  return gps.filter((point) => point.timecodeMs >= startMs && point.timecodeMs <= endMs);
}

export function gpsPointsInWindow(gps: MyChronGpsPoint[], startMs: number, endMs: number) {
  return gpsInWindow(gps, startMs, endMs);
}

export function analyzeLap(
  session: MyChronSessionExport,
  targetLapNumber: number,
  exclusions: LapExclusion[] = [],
  options?: {
    windowOverride?: Pick<ReplayLapWindow, 'startMs' | 'endMs' | 'durationSec'>;
    rankPool?: Array<{ lapNumber: number; durationSec: number }>;
  },
) {
  const laps = racingLaps(session.laps);
  const rawLaps = resolveRawLoggerLaps(session);
  const activeLaps = racingLapsForAnalysis(session, exclusions);
  const lapIndex = laps.findIndex((lap) => lap.lapNumber === targetLapNumber);
  const rawLapIndex = rawLaps.findIndex((lap) => lap.lapNumber === targetLapNumber);
  if (lapIndex < 0 && rawLapIndex < 0 && !options?.windowOverride) {
    return null;
  }

  const selectedLap =
    lapIndex >= 0 ? laps[lapIndex]! : rawLapIndex >= 0 ? rawLaps[rawLapIndex]! : null;
  const startMs = options?.windowOverride?.startMs ?? lapWindow(selectedLap ?? rawLaps[0]!).startMs;
  const endMs = options?.windowOverride?.endMs ?? lapWindow(selectedLap ?? rawLaps[0]!).endMs;
  const durationSec =
    options?.windowOverride?.durationSec ?? (selectedLap ?? rawLaps[0]!).durationMs / 1000;
  const lap: MyChronLapRecord = selectedLap
    ? selectedLap
    : {
        segment: 0,
        lapNumber: targetLapNumber,
        durationMs: durationSec * 1000,
        endTimeMs: endMs,
      };
  const rankPool = options?.rankPool;
  const ranked = rankPool?.length
    ? [...rankPool].sort((left, right) => left.durationSec - right.durationSec)
    : [...activeLaps].sort((left, right) => left.durationMs - right.durationMs);
  const bestEntry = ranked[0] ?? null;
  const bestLap =
    bestEntry && rankPool?.length
      ? (activeLaps.find((entry) => entry.lapNumber === bestEntry.lapNumber) ?? lap)
      : (ranked[0] ?? lap);
  const rank = rankPool?.length
    ? ranked.findIndex((entry) => entry.lapNumber === targetLapNumber) + 1
    : activeLaps.some((entry) => entry.lapNumber === lap.lapNumber)
      ? ranked.findIndex((entry) => entry.lapNumber === lap.lapNumber) + 1
      : laps.length;
  const lapGps = gpsInWindow(session.gps, startMs, endMs);
  const speeds = lapGps.map((point) => point.speed * 3.6);
  const bestDurationSec = rankPool?.length
    ? rankPool.reduce((min, entry) => Math.min(min, entry.durationSec), Infinity)
    : (bestLap as MyChronLapRecord).durationMs / 1000;

  return {
    lap:
      lapIndex >= 0 ? lap : { ...lap, lapNumber: targetLapNumber, durationMs: durationSec * 1000 },
    lapIndex: lapIndex >= 0 ? lapIndex : 0,
    startMs,
    endMs,
    durationSec,
    rank: rank > 0 ? rank : laps.length,
    totalLaps: rankPool?.length ?? activeLaps.length,
    gapToBestSec:
      bestEntry && targetLapNumber !== bestEntry.lapNumber
        ? durationSec - bestDurationSec
        : targetLapNumber === bestEntry?.lapNumber
          ? null
          : null,
    idealLapSec: null,
    topSpeedKph: speeds.length
      ? speeds.reduce((max, value) => Math.max(max, value), -Infinity)
      : null,
    averageSpeedKph: speeds.length
      ? speeds.reduce((sum, value) => sum + value, 0) / speeds.length
      : null,
    maxRpm: maxInWindow(session.series.RPM, startMs, endMs),
    maxTempC: maxInWindow(session.series['TC 1'] ?? session.series['TC 2'], startMs, endMs),
    accXBaseline: medianInWindow(session.series.AccX, startMs, endMs),
  };
}

function nearestSeriesValue(
  series: MyChronSeriesSample[] | undefined,
  timecodeMs: number,
  thresholdMs = 250,
) {
  if (!series?.length) {
    return null;
  }

  let closest = series[0]!;
  let closestDelta = Math.abs(closest.timecodeMs - timecodeMs);

  for (const sample of series) {
    const delta = Math.abs(sample.timecodeMs - timecodeMs);
    if (delta < closestDelta) {
      closest = sample;
      closestDelta = delta;
    }
  }

  return closestDelta <= thresholdMs ? closest.value : null;
}

function interpolatedSeriesValue(
  series: MyChronSeriesSample[] | undefined,
  timecodeMs: number,
  maxGapMs = 1500,
) {
  if (!series?.length) {
    return null;
  }

  if (series.length === 1) {
    return Math.abs(series[0]!.timecodeMs - timecodeMs) <= maxGapMs ? series[0]!.value : null;
  }

  const first = series[0]!;
  if (timecodeMs <= first.timecodeMs) {
    return first.timecodeMs - timecodeMs <= maxGapMs ? first.value : null;
  }

  const last = series.at(-1)!;
  if (timecodeMs >= last.timecodeMs) {
    return timecodeMs - last.timecodeMs <= maxGapMs ? last.value : null;
  }

  let lo = 1;
  let hi = series.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (series[mid]!.timecodeMs < timecodeMs) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  const next = series[lo]!;
  const prev = series[lo - 1]!;
  const span = next.timecodeMs - prev.timecodeMs;
  if (span <= 0 || span > maxGapMs) {
    return nearestSeriesValue(series, timecodeMs, maxGapMs);
  }

  const ratio = (timecodeMs - prev.timecodeMs) / span;
  return prev.value + (next.value - prev.value) * ratio;
}

function interpolateGpsSpeedMs(gps: MyChronGpsPoint[], timecodeMs: number): number | null {
  if (gps.length === 0) {
    return null;
  }

  if (gps.length === 1) {
    return gps[0]!.speed;
  }

  const first = gps[0]!;
  if (timecodeMs <= first.timecodeMs) {
    return first.speed;
  }

  const last = gps.at(-1)!;
  if (timecodeMs >= last.timecodeMs) {
    return last.speed;
  }

  let lo = 1;
  let hi = gps.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (gps[mid]!.timecodeMs < timecodeMs) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  const next = gps[lo]!;
  const prev = gps[lo - 1]!;
  const span = next.timecodeMs - prev.timecodeMs;
  const t = span === 0 ? 0 : (timecodeMs - prev.timecodeMs) / span;

  return prev.speed + (next.speed - prev.speed) * t;
}

export function speedKphAtTime(session: MyChronSessionExport, timecodeMs: number): number | null {
  const wheelKph = interpolatedSeriesValue(session.series.WSpd, timecodeMs);
  if (wheelKph !== null && wheelKph > 0.5) {
    return wheelKph;
  }

  const gpsSpeedMs = interpolateGpsSpeedMs(session.gps, timecodeMs);
  if (gpsSpeedMs === null) {
    return wheelKph;
  }

  return gpsSpeedMs * 3.6;
}

function transformSeriesSampleValue(
  shortName: string,
  raw: number,
  accXBaseline: number | null,
): number {
  if (shortName === 'AccX') {
    return driverLongitudinalG(raw, accXBaseline) ?? raw;
  }

  if (shortName === 'AccY') {
    return driverLateralG(raw) ?? raw;
  }

  return raw;
}

export function seriesValueAtTime(
  session: MyChronSessionExport,
  shortName: string,
  timecodeMs: number,
  accXBaseline: number | null = null,
): number | null {
  const raw = interpolatedSeriesValue(session.series[shortName], timecodeMs);
  if (raw === null) {
    return null;
  }

  return transformSeriesSampleValue(shortName, raw, accXBaseline);
}

export type ReplayLapWindow = {
  lapNumber: number;
  startMs: number;
  endMs: number;
  durationSec: number;
};
